import { NavigationState } from '@react-navigation/native';
import { ModalScreens } from 'components/Navigation/types';
import { AnyAction } from 'redux';
import {
  FileBeingAdded,
  FileSourceType,
  FileType,
  FileTypeGroupsEnum,
} from 'service/file/types';
import {
  Alert,
  AppState,
  BarCodeScanner,
  Confirm,
  Modal,
} from 'store/app/types';
import { Asset, AssetFromSync } from 'store/assets/types';
import { PinNew } from 'store/plan/types';
import { AssessmentTemplate } from 'store/remote/types';
import { createAction } from 'typesafe-actions';

export const doAppMerge = createAction('doAppMerge')<Partial<AppState>>();

export const OPEN_NATIVE_APP_SETTINGS = createAction(
  '@APP/OPEN_NATIVE_APP_SETTINGS',
)();

export const doSetAppType = createAction('doSetAppType')<AppState['appType']>();

export const doSetSidebar =
  createAction('doSetSidebar')<Partial<AppState['sidebar']>>();

export const doSetOfflineOnly =
  createAction('doSetOfflineOnly')<AppState['offlineOnly']>();

export const setAppPages = createAction('setAppPages')<boolean>();

export const SET_APP_INSTANCE = createAction('@APP/SET_APP_INSTANCE')<string>();

export const NAVIGATE_MODAL = createAction('@APP/NAVIGATE_MODAL')<
  Modal & { size?: 'small' | 'fullscreen' | 'large' }
>();
export const NAVIGATE_MODAL_BACK = createAction(
  '@APP/NAVIGATE_MODAL_BACK',
  (closeIfInList?: ModalScreens[]) => closeIfInList,
)();

export const doShowSyncModal = createAction('doShowSyncModal')<{
  downloadCleanData: boolean;
}>();

export const doHideSyncModal = createAction('doHideSyncModal')();

export const DO_IMAGE_SELECT = createAction('@APP/DO_IMAGE_SELECT')<{
  action: AnyAction;
  source: FileSourceType.CAMERA | FileSourceType.IMAGE;
}>();

export const OPEN_BARCODE_VIEWER = createAction('@APP/OPEN_BARCODE_VIEWER')<{
  data: string;
  deleteKey?: string;
}>();

export const OPEN_BARCODE_SCANNER = createAction('@APP/OPEN_BARCODE_SCANNER')<
  Omit<BarCodeScanner, 'instance'>
>();

export const eventBarcodeSelected = createAction(
  '@EVENT/eventBarcodeSelected',
)<{ key: string; data: string }>();

export const eventPinSelected = createAction('@EVENT/eventPinSelected')<{
  key: string;
  data: PinNew | null;
}>();

export const eventAseSelectFindingQuestion = createAction(
  '@EVENT/eventAseSelectFindingQuestion',
)<{
  key: string;
  data: {
    questionId: string | null;
    sectionId: string | null;
  };
}>();

export const eventAseUpdateRiskRating = createAction(
  '@EVENT/eventAseUpdateRiskRating',
)<{
  key: string;
  data: {
    riskLikelihoodId: string;
    riskSeverityId: string;
    riskChangeReason: boolean;
    riskReason: string;
  };
}>();

export const eventFieldSettingUpdateGroupCheck = createAction(
  '@EVENT/eventFieldSettingUpdateGroupCheck',
)<{
  key: string;
  data: {
    id: string;
    name: string;
    typeId: string;
    action: 'create' | 'delete' | null;
  };
}>();

export const eventAseUpdateAuthor = createAction(
  '@EVENT/eventAseUpdateAuthor',
)<{
  key: string;
  data: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}>();

export const eventModalBackdropPress = createAction(
  'eventModalBackdropPress',
)();

export const CLOSE_BARCODE_SCANNER = createAction(
  '@APP/CLOSE_BARCODE_SCANNER',
)<{ data: string }>();

export const UNLOAD_BARCODE_SCANNER = createAction(
  '@APP/DO_LOAD_BARCODE_SCANNER',
)();

export const DO_NETWORK_CHECK_UNTIL_ONLINE = createAction(
  '@APP/DO_NETWORK_CHECK_UNTIL_ONLINE',
)<boolean>();

export const SET_LAST_ONLINE_TIME = createAction(
  '@APP/SET_LAST_ONLINE_TIME',
)<number>();
export const SET_LAST_OFFLINE_TIME = createAction(
  '@APP/SET_LAST_OFFLINE_TIME',
)<number>();

export const UPGRADE = createAction('@APP/UPGRADE')();

export const ALERT = createAction('@APP/ALERT')<Alert>();
export const CLEAR_ALERT = createAction('@APP/CLEAR_ALERT')();

export const ALERT_ERROR = createAction('@APP/ALERT_ERROR')<{
  error: Error;
  // The action to fire if is auth error
  action?: { type: string; payload?: unknown };
}>();

export const doShowSignatureDialog = createAction('doShowSignatureDialog')<{
  eventKey: string;
}>();
export const doHideSignatureDialog = createAction('doHideSignatureDialog')();

export const CONFIRM = createAction('@APP/CONFIRM')<Confirm>();
export const CLEAR_CONFIRM = createAction('@APP/CLEAR_CONFIRM')();

export const SHOW_BLOCKING_LOADER = createAction(
  '@APP/SHOW_BLOCKING_LOADER',
  (key?: string) => key,
)();

export const doShowLoading = createAction('doShowLoading')<
  | undefined
  | {
      text?: string;
      key?: string;
      waitKey?: string;
    }
>();

export const doLoaderCancel = createAction('doLoaderCancel')<{ key: string }>();

export const HIDE_BLOCKING_LOADER = createAction(
  '@APP/HIDE_BLOCKING_LOADER',
  (key?: string) => key,
)();

export const SET_NAVIGATION_STATE = createAction('@APP/SET_NAVIGATION_STATE')<
  NavigationState | undefined
>();

export const SET_GENERIC = createAction('@APP/SET_GENERIC')<{
  key: string;
  value?: unknown;
  temp?: boolean;
}>();

export const SET_GENERICS = createAction('@APP/SET_GENERICS')<
  {
    key: string;
    value?: unknown;
    temp?: boolean;
  }[]
>();

export const SET_INCOMING_VALUE = createAction('@APP/SET_INCOMING_VALUE')<{
  key: string;
  value?: string | string[] | number | number[] | Record<string, unknown>;
}>();

export const doToast = createAction('@APP/doToast')<string>();
export const doToastError = createAction('@APP/doToastError')<string>();
export const doRemoveToast = createAction('@APP/doRemoveToast')();

export const doFileShare = createAction('@APP/doFileShare')<{
  url: string;
  method?: 'GET' | 'POST';
  post?: Record<string, unknown>;
}>();
export const doFileShareDirect = createAction('@APP/doFileShareDirect')<{
  url: string;
  title: string;
  fileType: FileType;
}>();
export const doFilePreviewPdf = createAction('@APP/doFilePreviewPdf')<{
  url: string;
}>();

export const eventSelectScreenSelection = createAction(
  '@eventSelectScreenSelection',
)<{
  key: string;
  stringValues: string[];
  numberValues: number[];
}>();
export const eventSelectScreenChosen = createAction(
  '@eventSelectScreenChosen',
)<{
  key: string;
  stringValue: string | undefined;
  numberValue: number | undefined;
}>();
export const eventSelectScreenAdded = createAction('@eventSelectScreenAdded')<{
  key: string;
  stringValue: string | undefined;
  numberValue: number | undefined;
}>();

export const eventSelectScreenCancel = createAction(
  '@eventSelectScreenCancel',
)<{
  key: string;
}>();
export const eventSelectScreenClose = createAction('@eventSelectScreenClose')<{
  key: string;
}>();

export const eventReorderScreenDone = createAction('@eventReorderScreenDone')<{
  key: string;
  stringValues: string[];
  numberValues: number[];
}>();
export const eventReorderScreenClose = createAction(
  '@eventReorderScreenClose',
)<{
  key: string;
}>();

export const eventAssetDataUpdated = createAction('@eventAssetDataUpdated')<{
  route: 'local' | 'sync';
  assetId: string;
  data: Asset;
  updatedKeys: Partial<(keyof AssetFromSync)[]>;
}>();

export const eventAssessmentTemplateQuestionScreenDone = createAction(
  '@eventAssessmentTemplateQuestionScreenDone',
)<{
  key: string;
  value: number[];
}>();

export const eventAssessmentTemplateFieldScreenDone = createAction(
  '@eventAssessmentTemplateFieldScreenDone',
)<{
  key: string;
  value: AssessmentTemplate['fieldsConfig'];
}>();

export const doSetImagePreview = createAction('doSetImagePreview')<{
  image: string;
}>();
export const doUnsetImagePreview = createAction('doUnsetImagePreview')();

export const doSetSettingSavePhotosToMediaLibrary = createAction(
  'doSetSettingSavePhotosToMediaLibrary',
)<{ enable: boolean; ask: boolean }>();

export const eventTypeScrollToItem = createAction(
  '@EVENT/eventTypeScrollToItem',
)<{
  key: string;
  item: string;
  animated?: boolean;
  viewOffset?: number;
}>();

export const eventColorSelected = createAction('eventColorSelected')<{
  key: string;
  color: string;
}>();

export const eventFilesAdded = createAction('eventFilesAdded')<{
  key: string;
  files: FileBeingAdded[];
}>();

export const eventFilePickerFinished = createAction('eventFilePickerFinished')<{
  key: string;
}>();

export const eventTypeWebPhotoTaken = createAction('eventTypeWebPhotoTaken')<{
  key: string;
  files: FileBeingAdded[];
}>();

export const eventTypeWebPhotoCancelled = createAction(
  'eventTypeWebPhotoCancelled',
)<{
  key: string;
}>();

export const eventTypeFilesSelected = createAction('eventTypeFilesSelected')<{
  eventKey: string;
  fileIds: string[];
}>();

export const eventTypeSignatureTaken = createAction('eventTypeSignatureTaken')<{
  eventKey: string;
  signature: string;
}>();

export const doAppFileGetFilesFromPicker = createAction(
  'doAppFileGetFilesFromPicker',
)<
  {
    eventKey: string;
    multiSelect: boolean;
    allowedFileGroups: FileTypeGroupsEnum[] | null;
  } & (
    | {
        returnType: 'event';
        assetId?: undefined;
      }
    | {
        returnType: 'saveToAsset';
        assetId: string;
      }
  )
>();

export const doAppFileGetImagesFromPicker = createAction(
  'doAppFileGetImagesFromPicker',
)<
  {
    eventKey: string;
    multiSelect: boolean;
    allowVideo: boolean;
  } & (
    | {
        returnType: 'event';
        assetId?: undefined;
      }
    | {
        returnType: 'saveToAsset';
        assetId: string;
      }
  )
>();

export const doShowAppBackgroundImage = createAction(
  'doShowAppBackgroundImage',
)<boolean>();
